import React, { useCallback, useMemo } from "react";

import classNames from "classnames";

import { ReactComponent as Chevron32 } from "../../assets/icons/Chevron32.svg";
import { ReactComponent as Version16 } from "../../assets/icons/Version16.svg";
import { CustomSelect, type SelectValueType } from "../CustomSelect/CustomSelect";
import { Skeleton } from "../Skeleton/Skeleton";

import style from "./VersionSelector.module.css";

export type VersionSelectorIntervalProps = {
  label: string;
};

export type VersionSelectorActionsProps = {
  children: React.ReactNode;
};

export type VersionSelectorProps = {
  children?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onListboxOpenChange?: (isOpen: boolean) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
};

export type VersionSelectorRowProps = {
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  disabled?: boolean;
};

type VersionSelectorActionsComponent = React.FC<VersionSelectorActionsProps>;
const VersionSelectorRow: React.FC<VersionSelectorRowProps> = ({
  id,
  title,
  subtitle,
  meta,
  disabled,
}) => {
  const context = React.useContext(VersionSelectorContext);

  React.useLayoutEffect(() => {
    if (!context) return;
    context.registerRow({
      id,
      title,
      subtitle,
      meta,
      disabled,
    });
    return () => context.unregisterRow(id);
  }, [context, disabled, id, meta, subtitle, title]);

  return (
    <CustomSelect.Option value={id} disabled={disabled} className={style.option}>
      <div className={style.optionRow}>
        <div className={style.versionIcon}>
          <Version16 />
        </div>
        <div className={style.optionContent}>
          <span className={style.optionTitle}>{title}</span>
          {subtitle && <span className={style.optionSubtitle}>{subtitle}</span>}
          {meta && <span className={style.optionMeta}>{meta}</span>}
        </div>
      </div>
    </CustomSelect.Option>
  );
};

const VersionSelectorInterval: React.FC<VersionSelectorIntervalProps> = ({ label }) => {
  return (
    <div className={style.intervalRow} aria-hidden="true">
      {label}
    </div>
  );
};

type VersionSelectorActionsEntry = {
  id: string;
  content: React.ReactNode;
};

type VersionSelectorContextValue = {
  registerRow: (row: VersionSelectorRowProps) => void;
  unregisterRow: (id: string) => void;
  registerActions: (id: string, content: React.ReactNode) => void;
  unregisterActions: (id: string) => void;
};

const VersionSelectorContext = React.createContext<VersionSelectorContextValue | null>(null);

const VersionSelectorActions: VersionSelectorActionsComponent = ({ children }) => {
  const context = React.useContext(VersionSelectorContext);
  const actionsId = React.useId();

  React.useLayoutEffect(() => {
    if (!context) return;
    context.registerActions(actionsId, children);
    return () => context.unregisterActions(actionsId);
  }, [actionsId, children, context]);

  return null;
};

export const VersionSelector: React.FC<VersionSelectorProps> & {
  Row: typeof VersionSelectorRow;
  Interval: typeof VersionSelectorInterval;
  Actions: typeof VersionSelectorActions;
} = ({
  children,
  value,
  defaultValue,
  onChange,
  onListboxOpenChange,
  placeholder = "No version published yet",
  className,
  disabled,
  isLoading = false,
}) => {
  const [internalValue, setInternalValue] = React.useState<SelectValueType>(defaultValue ?? null);
  const [rowsById, setRowsById] = React.useState<Record<string, VersionSelectorRowProps>>({});
  const [actionsEntries, setActionsEntries] = React.useState<VersionSelectorActionsEntry[]>([]);
  const resolvedValue = value !== undefined ? value : internalValue;

  const registerRow = useCallback((row: VersionSelectorRowProps) => {
    setRowsById((prev) => ({ ...prev, [row.id]: row }));
  }, []);

  const unregisterRow = useCallback((id: string) => {
    setRowsById((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const registerActions = useCallback((id: string, content: React.ReactNode) => {
    setActionsEntries((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.id === id);
      if (existingIndex === -1) {
        return [...prev, { id, content }];
      }
      return prev.map((entry) => (entry.id === id ? { id, content } : entry));
    });
  }, []);

  const unregisterActions = useCallback((id: string) => {
    setActionsEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      registerRow,
      unregisterRow,
      registerActions,
      unregisterActions,
    }),
    [registerRow, registerActions, unregisterRow, unregisterActions]
  );

  const actionsContent = useMemo(() => {
    if (!actionsEntries.length) return null;
    return actionsEntries.map((entry) => (
      <React.Fragment key={entry.id}>{entry.content}</React.Fragment>
    ));
  }, [actionsEntries]);

  const handleChange = useCallback(
    (_e: unknown, newValue: SelectValueType) => {
      setInternalValue(newValue);
      if (typeof newValue === "string" && onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const handleListboxOpenChange = useCallback(
    (isOpen: boolean) => {
      onListboxOpenChange?.(isOpen);
    },
    [onListboxOpenChange]
  );

  const selectedVersion = useMemo(() => {
    if (typeof resolvedValue !== "string") return null;
    return rowsById[resolvedValue] ?? null;
  }, [resolvedValue, rowsById]);

  const dropdownTriggerButton = (
    <div className={style.buttonContent}>
      <span className={style.buttonChevron}>
        <Chevron32 />
      </span>
      {selectedVersion ? (
        <div className={style.buttonText}>
          <span className={style.buttonTitle}>{selectedVersion.title}</span>
          {selectedVersion.subtitle && (
            <span className={style.buttonSubtitle}>{selectedVersion.subtitle}</span>
          )}
          {selectedVersion.meta && <span className={style.buttonMeta}>{selectedVersion.meta}</span>}
        </div>
      ) : (
        <span className={style.placeholder}>{placeholder}</span>
      )}
    </div>
  );

  const loadingTriggerButton = (
    <div className={style.buttonContent}>
      <span className={style.buttonChevron}>
        <Chevron32 />
      </span>
      <div className={style.buttonText}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="30%" height={12} />
        <Skeleton variant="text" width="10%" height={12} />
      </div>
    </div>
  );

  return (
    <VersionSelectorContext.Provider value={contextValue}>
      <div className={classNames(style.wrapper, className, actionsContent && style.hasActions)}>
        <div className={style.triggerRow}>
          <div className={style.selectWrapper}>
            {isLoading ? (
              <div className={style.loadingState}>{loadingTriggerButton}</div>
            ) : (
              <CustomSelect
                value={resolvedValue}
                onChange={handleChange}
                onListboxOpenChange={handleListboxOpenChange}
                disabled={disabled}
                className={style.select}
                placeholder={placeholder}
                renderValue={() => dropdownTriggerButton}
                hideIcon
                alignSelectedWithTrigger
              >
                {children}
              </CustomSelect>
            )}
          </div>
          {actionsContent && <div className={style.buttonActions}>{actionsContent}</div>}
        </div>
      </div>
    </VersionSelectorContext.Provider>
  );
};

VersionSelector.Row = VersionSelectorRow;
VersionSelector.Interval = VersionSelectorInterval;
VersionSelector.Actions = VersionSelectorActions;
